// Wraps navigation actions so that switching songs / views / closing
// the tab while the editor buffer is dirty prompts before discarding.
// The decision modal lives in App.tsx; this hook holds the pending
// action and the open/closed state.
//
// Trigger points (any place a stale buffer could be silently dropped):
//   - selecting a different song from the library list
//   - switching view (Home ↔ Editor)
//   - "+ New song"
//   - sign-out
//   - closing the tab (beforeunload)
//
// Wraps each call site with `guardThen(cb)`. If not dirty, runs `cb`
// inline. If dirty, opens the modal and stashes `cb` as the pending
// action.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

export type UnsavedPrompt = {
  open: boolean;
  /** Called when the user picks "Save and continue". Should commit the
   *  buffer to storage then call `proceed`. */
  onSave: () => void;
  /** Called when the user picks "Discard". Skips save and runs the
   *  pending action. */
  onDiscard: () => void;
  /** Called when the user picks "Cancel". Closes the modal without
   *  running anything. */
  onCancel: () => void;
};

export type GuardThen = (cb: () => void) => () => void;

/** Exposes guardThen to any descendant of the App tree (e.g.
 *  SignInPanel) so they can wrap destructive actions like sign-out
 *  without prop-drilling. The default no-ops are a safety net for
 *  components rendered in a tree without a provider (tests). */
const UnsavedGuardContext = createContext<{ guardThen: GuardThen }>({
  guardThen: (cb) => () => cb(),
});

export const UnsavedGuardProvider = UnsavedGuardContext.Provider;

export function useUnsavedGuardContext(): { guardThen: GuardThen } {
  return useContext(UnsavedGuardContext);
}

export function useUnsavedGuard(params: {
  dirty: boolean;
  /** Save returns false when it didn't actually persist (e.g. user is
   *  anonymous and was prompted to sign in). The guard treats a false
   *  return as "abort the navigation" so the user lands back where
   *  they were rather than losing work behind a closed modal. */
  save: () => boolean;
}): {
  /** Wrap a navigation callback so it goes through the modal when
   *  the buffer is dirty. Returns a no-arg function safe to pass to
   *  click handlers. */
  guardThen: (cb: () => void) => () => void;
  prompt: UnsavedPrompt;
} {
  const { dirty, save } = params;
  const [open, setOpen] = useState(false);
  const pendingRef = useRef<(() => void) | null>(null);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  const guardThen = useCallback(
    (cb: () => void) => () => {
      if (!dirtyRef.current) {
        cb();
        return;
      }
      pendingRef.current = cb;
      setOpen(true);
    },
    [],
  );

  const onSave = useCallback(() => {
    const persisted = save();
    if (!persisted) {
      // Save deferred (e.g. anon → sign-in modal). Close the prompt
      // but don't run the pending navigation; the buffer is still
      // dirty and we don't want to drop it behind the user's back.
      pendingRef.current = null;
      setOpen(false);
      return;
    }
    const cb = pendingRef.current;
    pendingRef.current = null;
    setOpen(false);
    cb?.();
  }, [save]);

  const onDiscard = useCallback(() => {
    const cb = pendingRef.current;
    pendingRef.current = null;
    setOpen(false);
    cb?.();
  }, []);

  const onCancel = useCallback(() => {
    pendingRef.current = null;
    setOpen(false);
  }, []);

  // beforeunload: native prompt on tab close. The browser shows its
  // own dialog; we just opt in by setting returnValue. Custom text is
  // ignored by modern browsers (they show a generic warning).
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  return {
    guardThen,
    prompt: { open, onSave, onDiscard, onCancel },
  };
}
